// #region imports

// --- Angular
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

// --- RxJs
import { Observable, throwError, BehaviorSubject, timeout } from 'rxjs';
import { switchMap, catchError, filter, take } from 'rxjs/operators';

// --- HS Code
import { OidcService } from '../services/account/oidc.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class TokenInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);


  // Configuration for token refresh
  private readonly TOKEN_EXPIRY_BUFFER_MINUTES = 5; // Refresh token 5 minutes before expiry
  private readonly AUTH_ENDPOINTS = ['auth', 'token', 'login', 'logout', 'silent-renew'];
  private readonly PUBLIC_ENDPOINTS = ['/error', '/participantsvalidation', '/registrationvalidation', '/respondentvalidation/'
    , '/respondentdecline', '/antiforgerytoken', '/tokenvalidation'];
  private readonly REFRESH_TIMEOUT_MS = 10000; // 10 second timeout for token refresh

  constructor(private oidcService: OidcService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip token handling for auth-related requests
    if (this.shouldSkipTokenHandling(req)) {
      return next.handle(req);
    }

    return this.oidcService.identity$.pipe(
      switchMap(user => {
        if (user && this.isUserAuthenticated(user)) {
          // Check if token is about to expire (within 5 minutes)
          if (this.isTokenExpiringSoon(user)) {
            // Trigger silent refresh if not already refreshing
            if (!this.isRefreshing) {
              this.refreshToken();
            }
          }

          req = req.clone({
            setHeaders: {
              Authorization: `Bearer ${user.access_token}`
            }
          });
        } else if (user && user.expired) {
          // User exists but token is expired, try to refresh
          console.log('User token expired, attempting refresh...');
          if (!this.isRefreshing) {
            this.refreshToken();
          }
        } else {
          // No valid user, check if we need to redirect
          console.log('No valid user, checking authentication requirements...');

          // For API calls, always redirect to login if no user
          if (this.isApiCall(req)) {
            console.log('API call requires authentication, redirecting to login...');
            this.redirectToLogin();
            return throwError(() => new Error('Authentication required'));
          }

          // For page navigation, only redirect if it's a protected route
          if (this.requiresAuthentication(req)) {
            console.log('Protected route requires authentication, redirecting to login...');
            this.redirectToLogin();
            return throwError(() => new Error('Authentication required'));
          }
        }

        return next.handle(req).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return this.handle401Error(req, next);
            }
            return throwError(() => error);
          })
        );
      }),
      catchError((error) => {
        // If there's any error in the interceptor itself, don't break the app
        console.warn('Token interceptor error, proceeding without token:', error);
        // Continue with the original request without token
        return next.handle(req);
      })
    );
  }

  private shouldSkipTokenHandling(request: HttpRequest<any>): boolean {
    // Skip token handling for auth-related endpoints
    return this.AUTH_ENDPOINTS.some(endpoint => request.url.includes(endpoint)) ||
           this.PUBLIC_ENDPOINTS.some(endpoint => request.url.includes(endpoint));
  }

  private requiresAuthentication(request: HttpRequest<any>): boolean {
    // Skip authentication for public endpoints
    const publicEndpoints = ['/home', '/login', '/public', '/health', '/status'];

    // If it's the home page and no user, allow it (user can see public content)
    if (request.url.includes('/home') || request.url.includes('/')) {
      return false;
    }

    // For API calls, always require authentication
    if (request.url.includes('/api/') || request.url.includes('api.')) {
      return true;
    }

    return !publicEndpoints.some(endpoint => request.url.includes(endpoint));
  }

  private isApiCall(request: HttpRequest<any>): boolean {
    return request.url.includes('/api/') ||
           request.url.includes('api.') ||
           request.url.includes('http') ||
           request.url.includes('https');
  }

  private isTokenExpiringSoon(user: any): boolean {
    // Check if user has expiration time information
    if (!user.expires_at) {
      // If no expires_at, check if the user is marked as expired
      return user.expired === true;
    }

    const expirationTime = new Date(user.expires_at * 1000);
    const currentTime = new Date();
    const bufferTime = new Date(currentTime.getTime() + this.TOKEN_EXPIRY_BUFFER_MINUTES * 60 * 1000);

    return expirationTime <= bufferTime;
  }

  private refreshToken(): void {
    if (this.isRefreshing) {
      return; // Already refreshing
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    console.log('Initiating token refresh...');

    // Add timeout to prevent hanging
    const timeout = setTimeout(() => {
      if (this.isRefreshing) {
        console.warn('Token refresh timed out, resetting state');
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
      }
    }, this.REFRESH_TIMEOUT_MS);

    this.oidcService.signInSilent$().subscribe({
      next: (user) => {
        clearTimeout(timeout);
        console.log('Token refresh successful');
        this.isRefreshing = false;
        this.refreshTokenSubject.next(user);
      },
      error: (error) => {
        clearTimeout(timeout);
        console.warn('Token refresh failed:', error);
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);

        // If silent refresh fails, the user needs to re-authenticate
        // Don't redirect here - let the 401 handler deal with it
        // This prevents redirect loops
      }
    });
  }



  private isUserAuthenticated(user: any): boolean {
    return user && !user.expired && user.access_token;
  }

  private redirectToLogin(): void {
    console.log('Redirecting to OIDC login...');
    // Use the OIDC service to trigger the proper authentication flow
    this.oidcService.signInRedirect();
  }



  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log('Handling 401 error, attempting token refresh...');

    // If not already refreshing, start refresh
    if (!this.isRefreshing) {
      this.refreshToken();
    }

    // Wait for token refresh with timeout
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      timeout(this.REFRESH_TIMEOUT_MS),
      switchMap(() => {
        console.log('Token refreshed, retrying original request...');
        // Retry with new token
        return this.oidcService.identity$.pipe(
          take(1),
          switchMap(user => {
            if (user && this.isUserAuthenticated(user)) {
              const newRequest = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${user.access_token}`
                }
              });
              return next.handle(newRequest);
            }
            // If no valid user after refresh, redirect to login
            console.warn('No valid user after token refresh, redirecting to login');
            this.redirectToLogin();
            return throwError(() => new Error('Authentication required'));
          })
        );
      }),
      catchError((error) => {
        console.warn('Token refresh failed, redirecting to login:', error);
        // If refresh fails, redirect to login instead of continuing without token
        this.redirectToLogin();
        return throwError(() => new Error('Authentication required'));
      })
    );
  }
}
